import { useState, useEffect, useCallback } from "react";
import api from "../api/client";
import { useToast } from "../hooks/use-toast";
import CalendarMonth from "./CalendarMonth";
import CalendarSyncGuide from "./CalendarSyncGuide";

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const fmt = (isoDate) => {
  const d = new Date(`${isoDate}T12:00:00`);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

export default function AgendaView({ studentId = null, showSubscription = true }) {
  const [events, setEvents] = useState([]);
  const [nextAppt, setNextAppt] = useState(null);
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    const qs = studentId ? `?student_id=${studentId}` : "";
    try {
      const [ev, na] = await Promise.all([
        api.get(`/calendar/events${qs}`),
        api.get(`/calendar/next-appointment${qs}`),
      ]);
      setEvents(ev.data.events || []);
      setNextAppt(na.data.appointment);
    } catch (e) {
      toast({ variant: "destructive", title: "Impossible de charger l'agenda" });
    } finally {
      setLoading(false);
    }
  }, [studentId, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!showSubscription) return;
    api.get("/calendar/feed-token").then(r => setFeed(r.data)).catch(() => setFeed(null));
  }, [showSubscription]);

  const feedUrl = feed ? `${window.location.origin}${feed.path}` : "";

  const copyFeed = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      toast({ title: "Lien copié", description: "Collez-le dans Google Agenda, Outlook ou Apple Calendrier." });
    } catch (e) {
      const input = document.querySelector('[data-testid="agenda-feed-url"]');
      if (input) {
        input.select();
        document.execCommand("copy");
        toast({ title: "Lien copié", description: "Collez-le dans votre agenda." });
      } else {
        toast({ variant: "destructive", title: "Copie impossible", description: feedUrl });
      }
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Générer un nouveau lien ? L'ancien cessera de fonctionner immédiatement.")) return;
    setBusy(true);
    try {
      const r = await api.post("/calendar/feed-token/regenerate");
      setFeed(r.data);
      toast({ title: "Nouveau lien généré" });
    } finally { setBusy(false); }
  };

  const downloadIcs = async (event) => {
    const path = event.type === "appointment"
      ? `/calendar/ics/appointment/${event.id}`
      : `/calendar/ics/substage/${event.id}`;
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `odys-${event.type}-${event.date}.ics`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Événement téléchargé", description: "Ouvrez le fichier pour l'ajouter à votre agenda." });
    } catch (e) {
      toast({ variant: "destructive", title: "Téléchargement impossible" });
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter(e => e.date >= today && !e.done).slice(0, 12);
  const late = events.filter(e => e.date < today && e.type === "deadline" && !e.done);

  return (
    <div className="space-y-4" data-testid="agenda-view">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="glass-card rounded-2xl p-5" data-testid="next-appointment-card">
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Prochain rendez-vous</p>
          {nextAppt ? (
            <>
              <p className="font-semibold text-gray-900" data-testid="next-appointment-name">{nextAppt.name}</p>
              <p className="text-sm text-[#C9A86A] mt-1" data-testid="next-appointment-date">{nextAppt.start_local}</p>
              <button
                data-testid="next-appointment-ics-btn"
                onClick={() => downloadIcs({ id: nextAppt.id, type: "appointment", date: nextAppt.start.slice(0, 10) })}
                className="btn-secondary mt-4 text-xs"
              >
                Ajouter à mon agenda
              </button>
            </>
          ) : (
            <p className="text-sm text-gray-400" data-testid="next-appointment-empty">Aucun rendez-vous à venir</p>
          )}
        </div>

        <div className="glass-card rounded-2xl p-5">
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Échéances à venir</p>
          <p className="text-3xl font-bold text-gray-900" data-testid="agenda-upcoming-count">
            {upcoming.filter(e => e.type === "deadline").length}
          </p>
          <p className="text-xs text-gray-500 mt-1">sur les prochaines semaines</p>
        </div>

        <div className="glass-card rounded-2xl p-5">
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">En retard</p>
          <p className={`text-3xl font-bold ${late.length ? "text-red-500" : "text-gray-900"}`} data-testid="agenda-late-count">
            {late.length}
          </p>
          <p className="text-xs text-gray-500 mt-1">échéance(s) dépassée(s)</p>
        </div>
      </div>

      {loading ? (
        <div className="skeleton h-72 rounded-2xl" />
      ) : (
        <CalendarMonth events={events} onSelectEvent={downloadIcs} />
      )}

      <div className="glass-card rounded-2xl p-5" data-testid="agenda-upcoming-list">
        <h3 className="font-semibold text-gray-900 mb-4" style={{ fontFamily: "Georgia, serif" }}>À venir</h3>
        {upcoming.length === 0 ? (
          <p className="text-sm text-gray-400">Rien de prévu pour l'instant.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {upcoming.map(e => (
              <div key={`${e.type}-${e.id}`} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {e.student_name ? <span className="text-[#C9A86A]">{e.student_name} · </span> : null}
                    {e.title}
                  </p>
                  <p className="text-xs text-gray-500">
                    {e.type === "appointment" ? "Rendez-vous" : e.stage} — {fmt(e.date)}{e.time ? ` à ${e.time}` : ""}
                  </p>
                </div>
                <button
                  data-testid={`agenda-ics-btn-${e.id}`}
                  onClick={() => downloadIcs(e)}
                  className="btn-secondary text-xs shrink-0"
                >
                  .ics
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showSubscription && (
        <div className="glass-card rounded-2xl p-5" data-testid="agenda-subscription-card">
          <h3 className="font-semibold text-gray-900 mb-1" style={{ fontFamily: "Georgia, serif" }}>
            Synchroniser avec mon agenda
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Ajoutez ce lien privé une fois dans votre agenda habituel : vos échéances de sous-étapes et vos
            rendez-vous s'y afficheront et resteront à jour automatiquement (lecture seule).
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="agenda-feed-url"
              readOnly
              value={feedUrl}
              className="input-dark flex-1 text-xs"
              onFocus={e => e.target.select()}
            />
            <button data-testid="agenda-copy-feed-btn" onClick={copyFeed} className="btn-primary text-sm">Copier le lien</button>
          </div>
          <CalendarSyncGuide />
          <button
            data-testid="agenda-regenerate-feed-btn"
            onClick={regenerate}
            disabled={busy}
            className="text-xs text-red-600 hover:underline mt-3"
          >
            Régénérer le lien (révoque l'ancien)
          </button>
        </div>
      )}
    </div>
  );
}
